import { ref } from 'vue'

// ── Screen context ────────────────────────────────────────────────────────────
//
// What the user is looking at, so "cancel this one" means something.
//
// The shell contributes the generic part (route, title, language). Each module
// registers a provider per view that serialises whatever that view is showing.
// The module decides what to expose; the platform never reaches into its state.
//
// A snapshot is taken when a message is sent, not continuously: the context
// that matters is the one at the moment of asking.

const providers = new Map()
const globals = ref({})

/**
 * Registers what a view is showing. Call it from a view's setup and let the
 * returned function run on unmount.
 *
 *   onMounted(() => registerContext('quote-detail', () => ({
 *     quoteId: quote.value.id, status: quote.value.status, total: quote.value.total,
 *   })))
 *
 * @param {string} id       unique per view
 * @param {Function} provide  () => object — kept small: it travels with every message
 * @returns {Function} call to deregister
 */
export function registerContext(id, provide) {
  providers.set(id, provide)
  return () => providers.delete(id)
}

/** The shell's own contribution: route, page title, language. */
export function setGlobalContext(context) {
  globals.value = context
}

/**
 * The snapshot sent with a message.
 *
 * A provider that throws is skipped rather than breaking the send: a broken
 * view must not make the assistant unusable.
 */
export function collectContext() {
  const context = { ...globals.value }

  for (const [id, provide] of providers) {
    try {
      const value = provide()
      if (value && Object.keys(value).length) context[id] = value
    } catch {
      // Ignored on purpose — see above.
    }
  }

  return context
}
